"use client"

import React from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error: Error; reset: () => void }>
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error} reset={this.reset} />
      }

      return <DefaultErrorFallback error={this.state.error} reset={this.reset} />
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error
  reset: () => void
}

function DefaultErrorFallback({ error, reset }: ErrorFallbackProps) {
  return (
    <div className="min-h-[400px] flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Something went wrong
              </h3>
              <p className="text-sm text-red-700 mb-4">
                {error.message || "An unexpected error occurred. Please try again."}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={reset}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="ghost"
                  size="sm"
                >
                  Refresh page
                </Button>
              </div>
              {process.env.NODE_ENV === "development" && (
                <details className="mt-4">
                  <summary className="text-xs text-red-600 cursor-pointer">
                    Error details (development only)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 overflow-auto p-2 bg-red-100 rounded">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Form-specific error boundary with better context
export function FormErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallback={FormErrorFallback}
      onError={(error, errorInfo) => {
        // Log to error tracking service in production
        console.error("Form error:", { error, errorInfo })
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

function FormErrorFallback({ error, reset }: ErrorFallbackProps) {
  return (
    <div className="p-8 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-4">
        <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-amber-900 mb-2">
            Form Error
          </h3>
          <p className="text-sm text-amber-700 mb-4">
            There was a problem with the form. Your data has been saved locally.
            You can try again or refresh the page.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={reset}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
            <Button
              onClick={() => {
                // Save form data before reload
                const formData = localStorage.getItem("formDraft")
                if (formData) {
                  sessionStorage.setItem("formRecovery", formData)
                }
                window.location.reload()
              }}
              variant="outline"
              size="sm"
            >
              Reload with saved data
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
